import { forwardRef, memo } from 'react';
import { cva, VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/styles';

const menuItemColors = cva(
  'flex items-center rounded-sm w-full min-h-9 gap-2 px-2.5 py-2 font-body-md font-semibold transition-all cursor-pointer disabled:cursor-not-allowed',
  {
    variants: {
      color: {
        default: [
          'text-text-primary fill-icon-primary',
          'hover:bg-bg-inter-secondary-default active:bg-bg-inter-secondary-hover focus-visible:bg-bg-inter-secondary-hover',
          'disabled:bg-bg-inter-tertiary-default disabled:text-text-disabled disabled:text-text-disabled disabled:fill-icon-disabled',
          'dark:hover:bg-white/20 dark:active:bg-white/40 dark:focus-visible:bg-white/60',
          'dark:text-white dark:fill-icon-white',
        ].join(' '),
        destructive:
          'text-text-destructive fill-icon-error hover:bg-bg-inter-destructive-hover focus-visible:bg-bg-inter-destructive-pressed disabled:bg-bg-inter-tertiary-default disabled:text-text-disabled disabled:fill-icon-disabled',
      },
    },
    defaultVariants: {
      color: 'default',
    },
  },
);

interface MenuItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  active?: boolean;
}

export const MenuItemDescription = ({
  title,
  description,
  className,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn('flex flex-col items-start', className)}>
      <span className="text-left">{title}</span>

      <span className="font-body-sm text-text-tertiary font-normal text-left dark:text-white/60">{description}</span>
    </div>
  );
};

const MenuItemBase = (
  { children, className, color = 'default', active, ...rest }: MenuItemProps & VariantProps<typeof menuItemColors>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(menuItemColors({ color }), className, {
        'bg-bg-inter-tertiary-pressed dark:bg-white/40!': active,
      })}
      {...rest}
    >
      {children}
    </button>
  );
};

export const MenuItem = memo(forwardRef(MenuItemBase));
